import { Component, OnInit, OnDestroy, Injector, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { debounceTime, exhaustMap, filter, finalize } from 'rxjs/operators';
import { AutenticacionService, Usuario, Perfil } from './core/services/autenticacion.service';
import { MenuController, AlertController } from '@ionic/angular';
import { ConfigStore } from './store/config.store';
import { AuthStore } from './store/auth.store';
import {
  esRutaAccesoSinSesion,
  esRutaPublicaAuth,
  normalizarRuta,
} from './core/constants/rutas-publicas.const';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { NetworkService } from './shared/services/network.service';
import { HeaderService, UnidadOperativaItem, AreaItem } from './shared/services/header.service';
import { aplicarColorPrimarioEnDocumento, restaurarColorPrimarioPorDefecto } from './shared/config/colores-primarios.config';
import { aplicarPaletaTemaEnDocumento } from './shared/config/colores-tema.config';
import { getIniciales } from './shared/utils/iniciales';
import {
  calcularIdJerarquiaParaCargaAreas,
  calcularIdJerarquiaParaCargaRegiones,
  calcularIdJerarquiaParaCargaUO,
  esPerfilExtensionista,
  esPerfilEjecutivoIntegral,
  obtenerReglasHeader,
  ID_JERARQUIA_TODAS_REGIONES_NIVEL_CENTRAL,
  type ReglasHeader,
} from './shared/utils/perfil.util';
import { VisitaPanelService } from './shared/services/visita-panel.service';
import { VisitaOfflinePanelService } from './shared/services/visita-offline-panel.service';
import { ActividadGrupalPanelService } from './shared/services/actividad-grupal-panel.service';
import { RutaAccionPanelService } from './shared/services/ruta-accion-panel.service';
import { AsistenteIaEmbedService } from './shared/services/asistente-ia-embed.service';
import { GestionAccionesStateService } from './features/gestion/gestion-acciones.state';
import { planNegocioEstaEnEjecucion } from './shared/config/colores-plan-negocio.config';
import type { ProductorPlanNegocioItem } from './shared/services/negocio.service';
import { StatusBarThemeService } from './core/services/status-bar-theme.service';
import { NativeFloatingPanelSafeAreaService } from './core/services/native-floating-panel-safe-area.service';
import {
  construirMensajeNotificacionSolicitud48h,
  SolicitudService,
  type SolicitudSchemaApi,
} from './shared/services/solicitud.service';

const NOTIFICACIONES_MESES_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  usuarioActual: Usuario | null = null;
  perfilActivo: Perfil | null = null;
  isOnline: boolean = true;
  /** true cuando la ruta activa es login, callback, registro o UI de demo. */
  esRutaPublicaAuth = false;

  /** Header, menú lateral y padding del outlet solo en rutas autenticadas de la app. */
  get mostrarShellAutenticado(): boolean {
    return !!this.usuarioActual && !this.esRutaPublicaAuth;
  }

  /** Resalta el ítem «Ruta del día» en el menú offline (sin depender de routerLink). */
  menuRutaDelDiaActiva = false;
  /** Resalta «Configuración» en el menú offline. */
  menuConfiguracionActiva = false;
  
  // Control del split-pane para colapsar menú en pantallas grandes
  splitPaneDisabled: boolean = false;

  /** Si el sub-menú de "Gestión" está expandido (solo cuando hay productor activo). */
  gestionExpandido: boolean = false;
  /** Productor activo para construir los links del sub-menú de Gestión. */
  productorActivoParaMenu: ProductorPlanNegocioItem | null = null;

  mostrarMenuCopago(): boolean {
    return true;
  }

  /** Reglas de selectores según id_perfil (ver docs/HEADER_SELECTORES.md). */
  reglasHeader: ReglasHeader = obtenerReglasHeader(null);
  // Regiones (perfiles 4–6). id 1 = "Todas las Regiones" solo Nivel Central.
  regiones: { id: number; nombre: string }[] = [];
  idRegionSeleccionada: number = -1;
  cargandoRegiones: boolean = false;
  // Áreas (perfiles 2–6). -1 = Todas las Áreas
  areas: { id: number; nombre: string }[] = [];
  idAreaSeleccionada: number = -1;
  cargandoAreas: boolean = false;
  // Unidades Operativas (-1 = Todas las Unidades Operativas)
  unidadesOperativas: { id: number; nombre: string }[] = [];
  unidadOperativaActiva: number = -1;
  cargandoUnidadesOperativas: boolean = false;
  /** true cuando el perfil activo usa regla Extensionista (id_perfil = 1): solo se muestra Perfil + UOs (id_jerarquia = -1). */
  esExtensionista: boolean = false;
  /** true cuando el perfil activo es Ejecutivo Integral (id_perfil = 2). */
  esEjecutivoIntegral: boolean = false;

  /** Solicitudes últimas 48 h (GET `/solicitud/ultimas-48h`) para badge y popover; solo perfil Extensionista. */
  notificacionesUltimas48hItems: SolicitudSchemaApi[] = [];

  /** Iniciales del usuario para el avatar (nombre + primer apellido). */
  getIniciales = getIniciales;
  
  private subscriptions: Subscription = new Subscription();
  private mediaQueryListener: ((e: MediaQueryList | MediaQueryListEvent) => void) | null = null;
  private mediaQuery: MediaQueryList | null = null;
  /** Intervalo que comprueba periódicamente si el token expiró (redirige a login sin depender de peticiones HTTP). */
  private tokenExpiryCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  /** Evita mostrar el aviso por expiración de token más de una vez por ciclo (hasta que se renueve o expire). */
  private sesionAvisoMostradoToken = false;
  /** Evita mostrar el aviso por inactividad más de una vez por ciclo (hasta que se reinicie o expire). */
  private sesionAvisoMostradoInactividad = false;
  /** Referencia al alert de aviso de expiración abierto, si existe. */
  private sesionAvisoAlert: HTMLIonAlertElement | null = null;
  /** Segundos de anticipación (aprox. 1 minuto antes de la expiración efectiva del token). */
  private readonly SESION_AVISO_ANTICIPACION_S = 120;
  /** Inactividad máxima (ms) sin interacción que conlleva la revocación de sesión (15 min). */
  private readonly INACTIVIDAD_REVOCACION_MS = 15 * 60 * 1000;
  /** Anticipación del aviso por inactividad: 1 minuto antes de revocar la sesión. */
  private readonly INACTIVIDAD_AVISO_ANTICIPACION_MS = 60 * 1000;
  /** Listeners de interacción del usuario (para removerlos en ngOnDestroy). */
  private interactionListeners: Array<{ target: Window; type: string; listener: (e: Event) => void }> = [];
  /** Indica si el tema efectivo es claro (para aplicar color primario). */
  private temaEfectivoEsClaro = false;

  /** Disparadores consolidados para GET `/solicitud/ultimas-48h` (evita abortar peticiones en vuelo). */
  private readonly notificaciones48hRefresh$ = new Subject<'auto' | 'popover'>();
  private notificaciones48hFetchEnCurso = false;
  private notificaciones48hRefreshPendiente = false;

  // Servicios lazy-loaded para no bloquear inicialización
  private networkService?: NetworkService;
  private headerService?: HeaderService;

  private readonly autenticacionService = inject(AutenticacionService);
  private readonly router = inject(Router);
  private readonly menuController = inject(MenuController);
  private readonly configStore = inject(ConfigStore);
  private readonly authStore = inject(AuthStore);
  private readonly alertController = inject(AlertController);
  private readonly injector = inject(Injector);

  /** Servicio del panel de visitas (para template). */
  readonly panelService = inject(VisitaPanelService);
  /** Servicio del panel de agendamiento offline (para template). */
  readonly offlinePanelService = inject(VisitaOfflinePanelService);
  /** Servicio del panel overlay de acciones de Hoja de Ruta (para template). */
  readonly rutaAccionPanelService = inject(RutaAccionPanelService);
  /** Servicio del panel de asesorías especializadas (para template). */
  readonly actividadGrupalPanelService = inject(ActividadGrupalPanelService);
  /** Monta embed.js ia-demo cuando `asistenteIaEmbed.enabled` + sesión activos. */
  readonly asistenteIaEmbedService = inject(AsistenteIaEmbedService);
  /** Servicio de estado de gestión (para saber si hay productor activo). */
  private readonly gestionStateService = inject(GestionAccionesStateService);
  private readonly statusBarThemeService = inject(StatusBarThemeService);
  private readonly nativeFloatingPanelSafeArea = inject(NativeFloatingPanelSafeAreaService);
  private readonly solicitudService = inject(SolicitudService);

  ngOnInit() {
    this.nativeFloatingPanelSafeArea.init();
    this.actualizarEsRutaPublicaAuth();
    this.iniciarVigilanciaInactividad();

    // Aplicar paleta de tema (secondary, tertiary, success, etc.) y luego el tema (primary + dark/light)
    aplicarPaletaTemaEnDocumento();
    // Aplicar tema inmediatamente (crítico para UX)
    const temaInicial = this.configStore.obtenerTema();
    this.aplicarTema(temaInicial);
    
    // Diferir suscripción a cambios de tema y color primario para no bloquear renderizado inicial
    requestAnimationFrame(() => {
      const temaSubscription = this.configStore.tema$.subscribe(tema => {
        this.aplicarTema(tema);
      });
      this.subscriptions.add(temaSubscription);
      const colorPrimarioSubscription = this.configStore.colorPrimario$.subscribe(() => {
        if (this.temaEfectivoEsClaro) {
          aplicarColorPrimarioEnDocumento(this.configStore.obtenerColorPrimario());
        }
      });
      this.subscriptions.add(colorPrimarioSubscription);
    });

    // Verificación de autenticación síncrona pero mínima (necesaria para routing)
    const estaAutenticado = this.autenticacionService.obtenerEstadoAutenticacion();
    const rutaActual = normalizarRuta(window.location.pathname);

    // Si no está autenticado y no está en una ruta pública, redirigir a login
    const esRutaPublica = esRutaAccesoSinSesion(rutaActual);

    if (!estaAutenticado && !esRutaPublica) {
      requestAnimationFrame(() => {
        this.navegarLoginORutaDelDia();
      });
    } else if (estaAutenticado) {
      // Obtener datos del usuario actual (síncrono, rápido)
      this.usuarioActual = this.autenticacionService.obtenerUsuarioActual();
      this.sincronizarMenuSegunShell();
      this.asistenteIaEmbedService.sincronizar();
      // Diferir carga de unidades operativas para no bloquear renderizado
      this.diferirOperacionesPesadas();
    }

    // Diferir todas las suscripciones para no bloquear renderizado inicial
    requestAnimationFrame(() => {
      const routerSub = this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      ).subscribe(() => {
        this.actualizarEsRutaPublicaAuth();
        this.sincronizarMenuSegunShell();
      });
      this.subscriptions.add(routerSub);

      // Suscribirse a cambios en el usuario
      const usuarioSubscription = this.autenticacionService.usuarioActual$.subscribe(usuario => {
        this.usuarioActual = usuario;
        // Habilitar/deshabilitar el menú según shell visible (no solo por usuario en memoria)
        this.sincronizarMenuSegunShell();
        this.asistenteIaEmbedService.sincronizar();
        // Tras invalidarSesionApiPreservandoCampo el usuario puede seguir en memoria (modo campo)
        // sin token; no cargar header ni notificaciones hasta tener sesión API vigente.
        if (usuario && this.puedeCargarDatosHeader()) {
          this.diferirOperacionesPesadas();
          this.refrescarNotificacionesUltimas48hSiAplica();
        } else if (!usuario) {
          this.menuController.enable(false, 'main-menu');
          this.areas = [];
          this.regiones = [];
          this.unidadesOperativas = [];
          this.notificacionesUltimas48hItems = [];
          // Limpiar estado de Gestión para que otro usuario no herede el productor ni el sub-menú abierto
          this.gestionStateService.clear();
          this.gestionExpandido = false;
          this.productorActivoParaMenu = null;

          // Si se cerró sesión estando en una ruta protegida, forzar redirección al login.
          // Esto evita quedar en una pantalla "a medio render" (contenido visible sin header/menú).
          // En carga profunda a /login2, el Router a veces aún no actualizó la URL en el primer tick;
          // usar también location.pathname para no redirigir erróneamente fuera del login.
          if (!this.estaEnRutaAccesoPublicoContraLocation()) {
            requestAnimationFrame(() => {
              this.navegarLoginORutaDelDia();
            });
          }
        }
      });
      this.subscriptions.add(usuarioSubscription);

      // Suscribirse a cambios en el perfil activo (recargar áreas y/o unidades operativas)
      const perfilSubscription = this.authStore.perfilActivo$.subscribe(perfil => {
        this.perfilActivo = perfil;
        this.esExtensionista = esPerfilExtensionista(perfil);
        this.esEjecutivoIntegral = esPerfilEjecutivoIntegral(perfil);
        this.reglasHeader = obtenerReglasHeader(perfil);
        if (!perfil || !this.puedeCargarDatosHeader()) {
          return;
        }
        if (this.esExtensionista) {
          this.regiones = [];
          this.areas = [];
          this.idAreaSeleccionada = -1;
          void this.cargarUnidadesOperativas();
        } else if (this.reglasHeader.mostrarSelectorRegion) {
          void this.cargarRegiones();
        } else {
          this.regiones = [];
          void this.cargarAreas();
        }
        this.refrescarNotificacionesUltimas48hSiAplica();
      });
      this.subscriptions.add(perfilSubscription);

      // Suscribirse a cambios en el área seleccionada (recargar UOs cuando no es Extensionista)
      const areaSubscription = this.authStore.idAreaSeleccionada$.subscribe(idArea => {
        this.idAreaSeleccionada = idArea;
        if (!this.esExtensionista && this.perfilActivo && this.puedeCargarDatosHeader()) {
          this.cargarUnidadesOperativas();
        }
      });
      this.subscriptions.add(areaSubscription);

      // Suscribirse a cambios del productor activo en Gestión (para sub-menú lateral).
      // Auto-expande SOLO cuando el productor cambia; si el mismo productor re-emite
      // (p.ej. al navegar dentro de /gestion) se respeta el estado manual del usuario.
      const gestionSub = this.gestionStateService.productorActivo$.subscribe(productor => {
        const cambioProductor = productor?.id_productor !== this.productorActivoParaMenu?.id_productor;
        this.productorActivoParaMenu = productor;
        if (cambioProductor) {
          this.gestionExpandido = productor != null;
        }
      });
      this.subscriptions.add(gestionSub);

      // Forzar expansión del submenú al elegir una acción desde el listado de Gestión.
      const expandirGestionSub = this.gestionStateService.solicitarExpandirMenuGestion$.subscribe(() => {
        this.gestionExpandido = true;
      });
      this.subscriptions.add(expandirGestionSub);

      // Cerrar paneles laterales (Agendar Visita, etc.) al ir a login/registro para que no queden abiertos
      const navSubscription = this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      ).subscribe(e => {
        const url = e.urlAfterRedirects ?? e.url ?? '';
        const path = url.split('?')[0];
        this.menuRutaDelDiaActiva = path === '/ruta-del-dia' || path.startsWith('/ruta-del-dia/');
        this.menuConfiguracionActiva = path === '/configuracion' || path.startsWith('/configuracion/');
        if (url === '/login' || url.startsWith('/login?') || url === '/login2' || url.startsWith('/login2?') || url === '/registro' || url.startsWith('/registro?')) {
          this.panelService.cerrar();
          this.panelService.alCerrarMenu();
          this.actividadGrupalPanelService.cerrar();
          this.actividadGrupalPanelService.alCerrarMenu();
          if (this.sesionAvisoAlert) {
            void this.sesionAvisoAlert.dismiss();
            this.sesionAvisoAlert = null;
          }
        }
      });
      this.subscriptions.add(navSubscription);

      // Suscribirse a cambios en la unidad operativa activa
      const unidadSubscription = this.authStore.unidadOperativaActiva$.subscribe(unidadId => {
        this.unidadOperativaActiva = unidadId;
      });
      this.subscriptions.add(unidadSubscription);

      this.configurarPipelineNotificaciones48h();

      const regionSubscription = this.authStore.idRegionSeleccionada$.subscribe(idRegion => {
        this.idRegionSeleccionada = idRegion;
        const perfil = this.perfilActivo;
        if (!perfil || !obtenerReglasHeader(perfil).mostrarSelectorRegion || !this.puedeCargarDatosHeader()) {
          return;
        }
        // Evitar áreas antes de tener región resuelta (cargarRegiones encadena cargarAreas al terminar).
        if (idRegion === -1) {
          return;
        }
        this.cargarAreas();
      });
      this.subscriptions.add(regionSubscription);

      // Comprobar periódicamente si el token expiró (p. ej. ventana abierta horas sin hacer peticiones).
      // Si hay refresh token, se intenta renovar la sesión de forma transparente antes de cerrar.
      const INTERVALO_EXPIRACION_MS = 60 * 1000; // cada 60 segundos
      this.tokenExpiryCheckIntervalId = setInterval(async () => {
        if (!this.authStore.obtenerEstadoAutenticacion()) return;
        if (this.router.url === '/login' || this.router.url.startsWith('/login?') || this.router.url === '/login2' || this.router.url.startsWith('/login2?')) return;
        const online = this.networkService?.isOnline() ?? true;
        if (this.authStore.omitirCierrePorTokenExpiradoSinRed(online)) return;

        // Aviso proactivo: faltan menos de ~2 minutos para que expire el access token
        // (aprox. 1 minuto antes de la expiración efectiva, que considera el margen
        // de seguridad de 60s de tokenExpirado) o queda menos de 1 minuto para revocar
        // la sesión por inactividad. Se muestra una sola vez por ciclo hasta
        // que se renueve o la sesión expire/revoca.
        // Prioridad: inactividad muestra su aviso; token muestra el suyo si no hay otro abierto.
        // Inactividad: revocar la sesión si supera el límite sin interacción (15 min).
        // No aplica en modo campo offline (se omite arriba con omitirCierrePorTokenExpiradoSinRed).
        if (this.authStore.inactividadSuperada(this.INACTIVIDAD_REVOCACION_MS)) {
          this.panelService.cerrar();
          this.panelService.alCerrarMenu();
          this.gestionStateService.clear();
          this.gestionExpandido = false;
          this.productorActivoParaMenu = null;
          if (this.sesionAvisoAlert) {
            await this.sesionAvisoAlert.dismiss();
            this.sesionAvisoAlert = null;
          }
          // Resetear flags de aviso para que no reaparezcan tras la navegación
          this.sesionAvisoMostradoInactividad = false;
          this.sesionAvisoMostradoToken = false;
          this.autenticacionService.cerrarSesion();
          return;
        }

        const avisoInactividadAntes = Math.max(0, this.INACTIVIDAD_REVOCACION_MS - this.INACTIVIDAD_AVISO_ANTICIPACION_MS);
        const inactividadCerca = this.authStore.inactividadSuperada(avisoInactividadAntes);
        const tokenCerca = this.authStore.faltanMenosDe(this.SESION_AVISO_ANTICIPACION_S);

        // Aviso por inactividad: tiene precedencia
        if (inactividadCerca && !this.sesionAvisoMostradoInactividad && !this.sesionAvisoMostradoToken && !this.sesionAvisoAlert) {
          if (this.authStore.obtenerRefreshToken()) {
            this.sesionAvisoMostradoInactividad = true;
            void this.mostrarAvisoExpiracionSesion('inactividad');
          }
        }
        // Aviso por expiración de token: solo si no hay aviso de inactividad mostrando
        if (tokenCerca && !this.sesionAvisoMostradoToken && !this.sesionAvisoMostradoInactividad && !this.sesionAvisoAlert) {
          if (this.authStore.obtenerRefreshToken()) {
            this.sesionAvisoMostradoToken = true;
            void this.mostrarAvisoExpiracionSesion('token');
          }
        }

        if (this.authStore.tokenExpirado()) {
          // No forzar cierre de sesión si ya hay un modal de aviso abierto;
          // permitir que el modal maneje la situación (el usuario puede responder
          // o dejar que pase el minuto de gracia para inactividad del aviso).
          if (this.sesionAvisoAlert) {
            return;
          }
          // Renovar automáticamente si hay refresh token disponible.
          const refreshToken = this.authStore.obtenerRefreshToken();
          if (refreshToken) {
            void this.autenticacionService.refrescarToken().then((ok) => {
              if (ok) {
                // Token renovado: permitimos avisar de nuevo en el próximo ciclo y
                // cerramos el aviso si aún estaba abierto.
                this.sesionAvisoMostradoToken = false;
                this.sesionAvisoMostradoInactividad = false;
                if (this.sesionAvisoAlert) {
                  void this.sesionAvisoAlert.dismiss();
                  this.sesionAvisoAlert = null;
                }
              } else {
                this.panelService.cerrar();
                this.panelService.alCerrarMenu();
                this.gestionStateService.clear();
                this.gestionExpandido = false;
                this.productorActivoParaMenu = null;
                this.authStore.invalidarSesionApiPreservandoCampo();
                this.navegarLoginORutaDelDia(true);
              }
            });
            return;
          }
          this.panelService.cerrar();
          this.panelService.alCerrarMenu();
          this.gestionStateService.clear();
          this.gestionExpandido = false;
          this.productorActivoParaMenu = null;
          this.authStore.invalidarSesionApiPreservandoCampo();
          this.navegarLoginORutaDelDia(true);
        }
      }, INTERVALO_EXPIRACION_MS);
    });

    // Diferir suscripción a red para no bloquear renderizado inicial
    this.diferirSuscripcionRed();
  }

  /**
   * Vigilancia de inactividad: escucha interacciones del usuario (pointerdown/keydown,
   * funcionan también en el WebView de Capacitor en iOS/Android) y actualiza la última
   * interacción en el store. El contador inicia al cargar la sesión: la carga cuenta
   * como actividad inicial.
   */
  private iniciarVigilanciaInactividad(): void {
    const registrar = (): void => this.authStore.registrarInteraccion();
    for (const tipo of ['pointerdown', 'keydown'] as const) {
      window.addEventListener(tipo, registrar);
      this.interactionListeners.push({ target: window, type: tipo, listener: registrar });
    }
    if (this.authStore.obtenerEstadoAutenticacion()) {
      this.authStore.iniciarContadorInactividad();
    }
  }

  /**
   * Muestra un aviso proactivo: por expiración de token o por inactividad.
   * Si el usuario no actúa, el refresh transparente del intervalo se encarga al expirar.
   */
  private async mostrarAvisoExpiracionSesion(tipo: 'token' | 'inactividad'): Promise<void> {
    if (this.sesionAvisoAlert) {
      return;
    }
    const esInactividad = tipo === 'inactividad';
    const alert = await this.alertController.create({
      header: esInactividad ? 'Sesión por inactividad' : 'Sesión por expirar',
      message: esInactividad
        ? 'Ha estado inactivo por 14 minutos. Su sesión se cerrará en 1 minuto. ¿Desea mantenerla?'
        : 'Su sesión caducará pronto (menos de 2 minutos). ¿Desea mantenerla activa?',
      backdropDismiss: false,
      buttons: [
        {
          text: 'Cerrar sesión',
          role: 'destructive',
          handler: () => {
            this.authStore.invalidarSesionApiPreservandoCampo();
            this.navegarLoginORutaDelDia();
            return true;
          }
        },
        {
          text: 'Mantener sesión',
          handler: () => {
            this.authStore.registrarInteraccion();
            void this.autenticacionService.refrescarToken().then((ok) => {
              if (ok) {
                this.sesionAvisoMostradoToken = false;
                this.sesionAvisoMostradoInactividad = false;
              } else {
                this.authStore.invalidarSesionApiPreservandoCampo();
                this.navegarLoginORutaDelDia(true);
              }
            });
            return true;
          }
        }
      ]
    });
    // Evitar que la alerta se muestre si la sesión fue cerrada durante la creación asíncrona de la misma (o si ya se está en login).
    if (!this.authStore.obtenerEstadoAutenticacion() || this.router.url.startsWith('/login')) {
      return;
    }

    this.sesionAvisoAlert = alert;
    await alert.present();
    alert.onDidDismiss().then(() => {
      this.sesionAvisoAlert = null;
    });
  }

  /**
   * Sin sesión API: si hay snapshot de campo y no hay red, Ruta del día; si no, login.
   */
  private navegarLoginORutaDelDia(sesionExpirada = false): void {
    void this.cargarServiciosPesados().then(() => {
      const online = this.networkService?.isOnline() ?? true;
      if (this.authStore.debeEntrarRutaDelDiaSinRed(online)) {
        void this.router.navigate(['/ruta-del-dia'], { replaceUrl: true });
        return;
      }
      void this.router.navigate(['/login'], {
        replaceUrl: true,
        ...(sesionExpirada ? { queryParams: { sesionExpirada: 'true' } } : {})
      });
    });
  }

  /**
   * Carga servicios pesados de forma lazy
   */
  private async cargarServiciosPesados(): Promise<void> {
    if (!this.networkService) {
      this.networkService = this.injector.get(NetworkService);
    }
    if (!this.headerService) {
      this.headerService = this.injector.get(HeaderService);
    }
  }

  /**
   * Diferir operaciones pesadas que no son críticas para el renderizado inicial
   */
  private diferirOperacionesPesadas(): void {
    // Usar requestIdleCallback si está disponible, sino setTimeout
    const runWhenIdle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 0));
    
    runWhenIdle(async () => {
      // Cargar servicios pesados primero
      await this.cargarServiciosPesados();
      
      // Verificar perfil activo (puede actualizar el store y disparar la suscripción a perfilActivo$)
      if (this.usuarioActual) {
        this.verificarPerfilActivo(this.usuarioActual);
      }
      // Sincronizar estado local; la carga de áreas/UOs la hace la suscripción a perfilActivo$
      this.perfilActivo = this.authStore.obtenerPerfilActivo();
      this.esExtensionista = esPerfilExtensionista(this.perfilActivo);
      this.esEjecutivoIntegral = esPerfilEjecutivoIntegral(this.perfilActivo);
      this.reglasHeader = obtenerReglasHeader(this.perfilActivo);
      this.idRegionSeleccionada = this.authStore.obtenerIdRegionSeleccionada();
      this.idAreaSeleccionada = this.authStore.obtenerIdAreaSeleccionada();
      this.unidadOperativaActiva = this.authStore.obtenerUnidadOperativaActiva();
      this.refrescarNotificacionesUltimas48hSiAplica();
    });
  }

  /**
   * Diferir suscripción a cambios de red
   */
  private diferirSuscripcionRed(): void {
    // Diferir carga y suscripción a cambios
    const runWhenIdle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 100));
    
    runWhenIdle(async () => {
      await this.cargarServiciosPesados();
      
      if (this.networkService) {
        // Inicializar estado de red
        this.isOnline = this.networkService.isOnline();
        let primeraEmisionRed = true;
        let estadoRedAnterior = this.networkService.isOnline();

        // Suscribirse a cambios (transiciones online/offline → dashboard ↔ ruta-del-dia)
        const networkSubscription = this.networkService.getNetworkStatus().subscribe(isOnline => {
          this.isOnline = isOnline;
          if (!isOnline) {
            this.notificacionesUltimas48hItems = [];
          } else {
            this.refrescarNotificacionesUltimas48hSiAplica();
          }
          if (!primeraEmisionRed) {
            this.redirigirTrasCambioRed(estadoRedAnterior, isOnline);
          } else {
            primeraEmisionRed = false;
          }
          estadoRedAnterior = isOnline;
        });
        this.subscriptions.add(networkSubscription);
      }
    });
  }

  /**
   * Menú offline: cierra el panel y navega (más fiable que solo `routerLink` dentro de `ion-menu-toggle`).
   */
  async navegarARutaDelDia(): Promise<void> {
    await this.menuController.close('main-menu');
    await this.router.navigate(['/ruta-del-dia'], { replaceUrl: true });
  }

  /**
   * Sin red: cualquier ruta no permitida offline → `/ruta-del-dia`.
   * Con red de nuevo en ruta del día → `/inicio`.
   * `/configuracion` y login no redirigen al perder red (simulación / reentrada).
   */
  private redirigirTrasCambioRed(estadoAnterior: boolean, online: boolean): void {
    const ruta = this.router.url.split('?')[0] || '';
    if (estadoAnterior && !online) {
      const rutasPermitidasOffline = [
        '/ruta-del-dia',
        '/configuracion',
        '/login',
        '/login2',
        '/callback',
      ];
      const permitida = rutasPermitidasOffline.some(
        r => ruta === r || ruta.startsWith(`${r}/`)
      );
      if (!permitida) {
        void this.router.navigate(['/ruta-del-dia'], { replaceUrl: true });
      }
      return;
    }
    if (!estadoAnterior && online) {
      if (ruta === '/ruta-del-dia' || ruta.startsWith('/ruta-del-dia/')) {
        void this.router.navigate(['/inicio'], { replaceUrl: true });
      }
    }
  }

  /** Cantidad para badge (mismo arreglo que el popover). */
  get notificacionesUltimas48hCount(): number {
    return this.notificacionesUltimas48hItems.length;
  }

  /** Texto del badge (tope 99+). */
  get etiquetaBadgeNotificaciones48h(): string {
    const n = this.notificacionesUltimas48hCount;
    if (n <= 0) return '';
    return n > 99 ? '99+' : String(n);
  }

  /**
   * Punto de estado en el listado: verde si la descripción es «Aprobada», rojo si «Rechazada».
   */
  notificacionesEstadoIndicadorClase(
    s: SolicitudSchemaApi
  ): 'notif-48h-punto--aprobada' | 'notif-48h-punto--rechazada' | null {
    const desc = (s.estado?.descripcion ?? '').trim();
    if (desc === 'Aprobada') return 'notif-48h-punto--aprobada';
    if (desc === 'Rechazada') return 'notif-48h-punto--rechazada';
    return null;
  }

  private notificacionesFechaCalendarioEspañol(iso: string | null): string {
    if (iso == null || !iso.trim()) {
      return '—';
    }
    const d = new Date(iso.trim());
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    const mes = NOTIFICACIONES_MESES_ES[d.getMonth()];
    return `${d.getDate()} de ${mes} de ${d.getFullYear()}`;
  }

  /** Línea principal según matriz tipo de solicitud × resultado (Aprobación/Reapertura × Aprobada/Rechazada). */
  notificacionesTextoPrincipal(s: SolicitudSchemaApi): string {
    return construirMensajeNotificacionSolicitud48h(s, iso =>
      this.notificacionesFechaCalendarioEspañol(iso)
    );
  }

  /**
   * Tiempo relativo solo desde `fecha_respuesta` respecto a ahora (minutos / horas).
   */
  notificacionesRelativaDesdeRespuesta(s: SolicitudSchemaApi): string | null {
    const raw = s.fecha_respuesta;
    if (raw == null || typeof raw !== 'string' || !raw.trim()) {
      return null;
    }
    const then = new Date(raw.trim());
    if (Number.isNaN(then.getTime())) {
      return null;
    }
    let diffMs = Date.now() - then.getTime();
    if (diffMs < 0) {
      diffMs = 0;
    }
    const minutesTotal = Math.floor(diffMs / 60000);
    if (minutesTotal < 1) {
      return 'Hace un momento';
    }
    if (minutesTotal < 60) {
      return `Hace ${minutesTotal} min`;
    }
    const hours = Math.floor(minutesTotal / 60);
    if (hours < 24) {
      return hours === 1 ? 'Hace 1 hora' : `Hace ${hours} horas`;
    }
    const days = Math.floor(hours / 24);
    return days === 1 ? 'Hace 1 día' : `Hace ${days} días`;
  }

  /** Refresca listado/badge si aplica perfil Extensionista, online, sesión API y hay `sub`. */
  refrescarNotificacionesUltimas48hSiAplica(): void {
    if (
      !this.isOnline ||
      !this.esExtensionista ||
      !this.usuarioActual?.sub?.trim() ||
      !this.puedeCargarDatosHeader()
    ) {
      this.notificacionesUltimas48hItems = [];
      return;
    }
    this.notificaciones48hRefresh$.next('auto');
  }

  /** Al abrir el popover, datos frescos del servidor. */
  onNotificacionesPopoverWillPresent(): void {
    if (
      !this.isOnline ||
      !this.esExtensionista ||
      !this.usuarioActual?.sub?.trim() ||
      !this.puedeCargarDatosHeader()
    ) {
      return;
    }
    if (this.notificaciones48hFetchEnCurso) {
      this.notificaciones48hRefreshPendiente = true;
      return;
    }
    this.notificaciones48hRefresh$.next('popover');
  }

  /** Una sola suscripción HTTP: debounce agrupa ráfagas de arranque; exhaustMap no aborta la petición activa. */
  private configurarPipelineNotificaciones48h(): void {
    const sub = this.notificaciones48hRefresh$
      .pipe(
        debounceTime(150),
        exhaustMap(() => this.ejecutarFetchNotificaciones48h())
      )
      .subscribe(items => {
        this.notificacionesUltimas48hItems = items;
      });
    this.subscriptions.add(sub);
  }

  private ejecutarFetchNotificaciones48h(): Observable<SolicitudSchemaApi[]> {
    const idUsuario = this.usuarioActual?.sub?.trim();
    if (
      !this.isOnline ||
      !this.esExtensionista ||
      !idUsuario ||
      !this.puedeCargarDatosHeader()
    ) {
      return of([]);
    }
    this.notificaciones48hFetchEnCurso = true;
    return this.solicitudService.obtenerSolicitudesUltimas48h(idUsuario).pipe(
      finalize(() => {
        this.notificaciones48hFetchEnCurso = false;
        if (this.notificaciones48hRefreshPendiente) {
          this.notificaciones48hRefreshPendiente = false;
          this.notificaciones48hRefresh$.next('auto');
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.notificaciones48hRefresh$.complete();
    for (const l of this.interactionListeners) {
      l.target.removeEventListener(l.type, l.listener);
    }
    this.interactionListeners = [];
    if (this.tokenExpiryCheckIntervalId != null) {
      clearInterval(this.tokenExpiryCheckIntervalId);
      this.tokenExpiryCheckIntervalId = null;
    }
    if (this.sesionAvisoAlert) {
      void this.sesionAvisoAlert.dismiss();
      this.sesionAvisoAlert = null;
    }
    if (this.mediaQuery && this.mediaQueryListener) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.mediaQueryListener);
      } else {
        this.mediaQuery.removeListener(this.mediaQueryListener);
      }
    }
  }

  /**
   * Aplica el tema inicial desde el store
   */
  aplicarTemaInicial() {
    const tema = this.configStore.obtenerTema();
    this.aplicarTema(tema);
  }

  /**
   * Aplica el tema a la aplicación
   */
  aplicarTema(tema: 'claro' | 'oscuro' | 'auto') {
    // Limpiar listener anterior si existe
    if (this.mediaQuery && this.mediaQueryListener) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.mediaQueryListener);
      } else {
        this.mediaQuery.removeListener(this.mediaQueryListener);
      }
      this.mediaQuery = null;
      this.mediaQueryListener = null;
    }
    
    const aplicarClaseDark = (aplicar: boolean) => {
      const htmlElement = document.documentElement;
      const bodyElement = document.body;
      this.temaEfectivoEsClaro = !aplicar;
      
      // Aplicar ambas clases: 'dark' y 'ion-palette-dark'
      if (aplicar) {
        htmlElement.classList.add('dark', 'ion-palette-dark');
        if (bodyElement) {
          bodyElement.classList.add('dark', 'ion-palette-dark');
        }
        restaurarColorPrimarioPorDefecto();
      } else {
        htmlElement.classList.remove('dark', 'ion-palette-dark');
        if (bodyElement) {
          bodyElement.classList.remove('dark', 'ion-palette-dark');
        }
        aplicarColorPrimarioEnDocumento(this.configStore.obtenerColorPrimario());
      }

      this.statusBarThemeService.sincronizarConModoOscuro(aplicar);
    };
    
    if (tema === 'auto') {
      // Detectar preferencia del sistema
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.mediaQueryListener = (e: MediaQueryList | MediaQueryListEvent) => {
        aplicarClaseDark(e.matches);
      };
      
      // Aplicar inmediatamente
      aplicarClaseDark(this.mediaQuery.matches);
      
      // Escuchar cambios en la preferencia del sistema
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.mediaQueryListener);
      } else {
        this.mediaQuery.addListener(this.mediaQueryListener);
      }
    } else if (tema === 'oscuro') {
      aplicarClaseDark(true);
    } else {
      aplicarClaseDark(false);
    }
  }

  /**
   * Cierra la sesión del usuario
   */
  async cerrarSesion() {
    const alert = await this.alertController.create({
      header: 'Cerrar Sesión',
      message: '¿Está seguro que desea cerrar sesión?',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          handler: () => {
            // No hacer nada, solo cerrar la alerta
          }
        },
        {
          text: 'Cerrar Sesión',
          role: 'confirm',
          handler: () => {
            this.menuController.close();
            this.panelService.cerrar();
            this.panelService.alCerrarMenu();
            this.autenticacionService.cerrarSesion();
          }
        }
      ]
    });

    await alert.present();
  }

  /**
   * Cambia el perfil activo del usuario.
   * Al cambiar de perfil, el selector de unidades operativas se resetea a "Todas" (id_jerarquia = -1).
   * Se normaliza el valor (ion-select puede devolver string) y se actualiza el estado local de inmediato.
   */
  onPerfilChange(event: any) {
    const raw = event?.detail?.value;
    if (raw == null) return;
    const perfilId = typeof raw === 'number' ? raw : Number(raw);
    if (this.usuarioActual?.perfiles) {
      const perfil = this.usuarioActual.perfiles.find(p => p.id_perfil === perfilId);
      if (perfil) {
        this.authStore.cambiarPerfilActivo(perfil);
        // Sincronizar estado local de inmediato para que la vista refleje el reset (área/UO a -1)
        this.idAreaSeleccionada = -1;
        this.idRegionSeleccionada = this.authStore.obtenerIdRegionSeleccionada();
        this.unidadOperativaActiva = -1;
      }
    }
  }

  /**
   * Cambia la región (perfiles 4–6). Resetea área y UO en el store.
   */
  onRegionChange(event: any) {
    const raw = event?.detail?.value;
    if (raw == null) return;
    const idRegion = typeof raw === 'number' ? raw : Number(raw);
    this.authStore.cambiarRegionSeleccionada(idRegion);
    this.idRegionSeleccionada = idRegion;
    this.idAreaSeleccionada = -1;
    this.unidadOperativaActiva = -1;
  }

  /**
   * Cambia el área seleccionada. Al cambiar, se resetea la unidad operativa y se recargan las UOs.
   * Se normaliza a número por si ion-select devuelve string.
   */
  onAreaChange(event: any) {
    const raw = event?.detail?.value;
    if (raw == null) return;
    const idArea = typeof raw === 'number' ? raw : Number(raw);
    this.authStore.cambiarAreaSeleccionada(idArea);
    this.unidadOperativaActiva = -1;
  }

  /**
   * True solo con sesión API vigente (token). El snapshot de modo campo puede dejar
   * usuario/perfil en memoria sin JWT; en ese caso no hay que llamar al header (403).
   */
  private puedeCargarDatosHeader(): boolean {
    return this.authStore.obtenerEstadoAutenticacion() && !!this.authStore.obtenerToken();
  }

  /**
   * Carga regiones desde la API (perfiles 4–6). Perfil 6 antepone "Todas las Regiones" (id = 1).
   * Luego encadena cargarAreas().
   */
  private async cargarRegiones(): Promise<void> {
    if (this.cargandoRegiones) return;
    if (!this.puedeCargarDatosHeader()) return;
    await this.cargarServiciosPesados();
    if (!this.headerService) return;
    if (!(this.networkService?.isOnline() ?? true)) {
      return;
    }

    const perfil = this.authStore.obtenerPerfilActivo();
    if (!perfil || perfil.id_perfil == null) {
      this.regiones = [];
      this.cargarAreas();
      return;
    }
    const reglas = obtenerReglasHeader(perfil);
    if (!reglas.mostrarSelectorRegion) {
      this.regiones = [];
      this.cargarAreas();
      return;
    }

    this.cargandoRegiones = true;
    const idJerarquia = calcularIdJerarquiaParaCargaRegiones(perfil);

    this.headerService.obtenerRegiones(perfil.id_perfil, idJerarquia).subscribe({
      next: lista => {
        const arr = Array.isArray(lista) ? lista : [];
        const mapeadas = arr.map(r => ({ id: r.id_jerarquia, nombre: r.nombre }));
        let opciones = mapeadas;
        if (reglas.opcionTodasLasRegionesNivelCentral) {
          opciones = [
            { id: ID_JERARQUIA_TODAS_REGIONES_NIVEL_CENTRAL, nombre: 'Todas las Regiones' },
            ...mapeadas,
          ];
        }
        if (opciones.length === 0) {
          this.regiones = [];
          this.cargandoRegiones = false;
          this.cargarAreas();
          return;
        }
        if (opciones.length === 1) {
          this.regiones = opciones;
          this.authStore.cambiarRegionSeleccionada(opciones[0].id);
          this.idRegionSeleccionada = opciones[0].id;
          this.cargandoRegiones = false;
          this.cargarAreas();
          return;
        }
        this.regiones = opciones;
        const guardada = this.authStore.obtenerIdRegionSeleccionada();
        const valida = opciones.some(o => o.id === guardada);
        if (!valida) {
          if (reglas.opcionTodasLasRegionesNivelCentral) {
            this.authStore.cambiarRegionSeleccionada(ID_JERARQUIA_TODAS_REGIONES_NIVEL_CENTRAL);
          } else {
            this.authStore.cambiarRegionSeleccionada(opciones[0].id);
          }
        }
        this.idRegionSeleccionada = this.authStore.obtenerIdRegionSeleccionada();
        this.cargandoRegiones = false;
        this.cargarAreas();
      },
      error: () => {
        this.regiones = [];
        this.cargandoRegiones = false;
        this.cargarAreas();
      },
    });
  }

  /**
   * Carga las Áreas desde la API (perfiles con mostrarSelectorArea).
   * Una sola área: chip y preselección. Varias: "Todas las Áreas" al inicio. Tras cargar, carga UOs.
   */
  private async cargarAreas(): Promise<void> {
    if (this.cargandoAreas) return;
    if (!this.puedeCargarDatosHeader()) return;
    await this.cargarServiciosPesados();
    if (!this.headerService) return;
    if (!(this.networkService?.isOnline() ?? true)) {
      return;
    }

    const perfil = this.authStore.obtenerPerfilActivo();
    if (!perfil || perfil.id_perfil == null) {
      this.areas = [];
      this.unidadesOperativas = [{ id: -1, nombre: 'Todas las Unidades Operativas' }];
      return;
    }
    if (!obtenerReglasHeader(perfil).mostrarSelectorArea) {
      this.areas = [];
      this.cargarUnidadesOperativas();
      return;
    }

    this.cargandoAreas = true;
    const idJerarquia = calcularIdJerarquiaParaCargaAreas(
      perfil,
      this.authStore.obtenerIdRegionSeleccionada()
    );

    this.headerService.obtenerAreas(perfil.id_perfil, idJerarquia).subscribe({
      next: (lista: AreaItem[]) => {
        const arr = Array.isArray(lista) ? lista : [];
        const mapeadas = arr.map(a => ({
          id: a.id_jerarquia,
          nombre: (a.descripcion ?? '').toUpperCase(),
        }));
        if (arr.length === 0) {
          this.areas = [];
        } else if (arr.length === 1) {
          // Una sola área: solo esa opción, chip sin selector ni "Todas las Áreas"
          this.areas = mapeadas;
          this.authStore.cambiarAreaSeleccionada(mapeadas[0].id);
        } else {
          const opcionTodas = { id: -1, nombre: 'Todas las Áreas' };
          this.areas = [opcionTodas, ...mapeadas];
          this.idAreaSeleccionada = this.authStore.obtenerIdAreaSeleccionada();
        }
        this.cargandoAreas = false;
        this.cargarUnidadesOperativas();
      },
      error: () => {
        this.areas = [];
        this.cargandoAreas = false;
        this.cargarUnidadesOperativas();
      }
    });
  }

  /**
   * Carga las Unidades Operativas desde la API.
   * El input es el ID del área seleccionada (idAreaSeleccionada): -1 = todas las áreas; otro = UOs de esa área.
   * Extensionista: sin áreas, se usa -1.
   */
  private async cargarUnidadesOperativas(): Promise<void> {
    if (this.cargandoUnidadesOperativas) return;
    if (!this.puedeCargarDatosHeader()) return;
    await this.cargarServiciosPesados();
    if (!this.headerService) return;
    if (!(this.networkService?.isOnline() ?? true)) {
      return;
    }

    const perfil = this.authStore.obtenerPerfilActivo();
    if (!perfil || perfil.id_perfil == null) {
      this.unidadesOperativas = [{ id: -1, nombre: 'Todas las Unidades Operativas' }];
      this.cargandoUnidadesOperativas = false;
      return;
    }

    const idJerarquia = calcularIdJerarquiaParaCargaUO(
      perfil,
      this.authStore.obtenerIdRegionSeleccionada(),
      this.authStore.obtenerIdAreaSeleccionada()
    );
    this.cargandoUnidadesOperativas = true;

    this.headerService.obtenerUnidadesOperativas(perfil.id_perfil, idJerarquia).subscribe({
      next: (unidades: UnidadOperativaItem[]) => {
        const unidadesApi = unidades.map(u => ({ id: u.id_jerarquia, nombre: u.descripcion }));
        if (unidadesApi.length === 1) {
          // Una sola UO: mostrar como chip (sin selector ni opción "Todas") y preseleccionarla
          this.unidadesOperativas = unidadesApi;
          this.authStore.cambiarUnidadOperativa(unidadesApi[0].id);
        } else {
          const opcionTodas = { id: -1, nombre: 'Todas las Unidades Operativas' };
          this.unidadesOperativas = [opcionTodas, ...unidadesApi];
          this.verificarUnidadOperativaActiva(unidadesApi);
        }
        this.cargandoUnidadesOperativas = false;
      },
      error: () => {
        this.unidadesOperativas = [{ id: -1, nombre: 'Todas las Unidades Operativas' }];
        this.authStore.cambiarUnidadOperativa(-1);
        this.cargandoUnidadesOperativas = false;
      }
    });
  }

  /**
   * Verifica si la unidad operativa guardada en el store sigue siendo válida.
   * Si no es válida, establece -1 (Todas las Unidades Operativas) por defecto.
   */
  private verificarUnidadOperativaActiva(unidadesApi: { id: number; nombre: string }[]) {
    const unidadGuardada = this.authStore.obtenerUnidadOperativaActiva();

    // -1 (Todas) siempre es válido
    if (unidadGuardada === -1) {
      return;
    }

    // Verificar si la unidad guardada existe en las unidades disponibles
    const unidadValida = unidadesApi.find(u => u.id === unidadGuardada);

    if (!unidadValida) {
      this.authStore.cambiarUnidadOperativa(-1);
    }
  }

  /**
   * Cambia la unidad operativa activa y la guarda en el store.
   * No se acepta el id del área como UO: si coincide con idAreaSeleccionada (p. ej. una sola área), se usa -1.
   * Se normaliza a número por si ion-select devuelve string.
   */
  onUnidadOperativaChange(event: any) {
    const raw = event?.detail?.value;
    if (raw == null) return;
    let unidadId = typeof raw === 'number' ? raw : Number(raw);
    const idArea = this.authStore.obtenerIdAreaSeleccionada();
    const idRegion = this.authStore.obtenerIdRegionSeleccionada();
    if (idArea !== -1 && unidadId === idArea) {
      unidadId = -1;
    }
    if (idRegion !== -1 && unidadId === idRegion) {
      unidadId = -1;
    }
    this.authStore.cambiarUnidadOperativa(unidadId);
  }

  /**
   * Verifica si hay un perfil activo válido, si no, selecciona el primero
   * También verifica que el perfil guardado siga siendo válido para el usuario actual
   */
  private verificarPerfilActivo(usuario: Usuario) {
    if (!usuario.perfiles || usuario.perfiles.length === 0) {
      // No hay perfiles disponibles, limpiar perfil activo
      if (this.authStore.obtenerPerfilActivo()) {
        this.authStore.cambiarPerfilActivo(null);
      }
      return;
    }

    const perfilActualGuardado = this.authStore.obtenerPerfilActivo();
    
    // Verificar si el perfil guardado es válido para el usuario actual
    if (perfilActualGuardado) {
      const perfilValido = usuario.perfiles.find(p => p.id_perfil === perfilActualGuardado.id_perfil);
      if (perfilValido) {
        // El perfil guardado es válido, no hacer nada
        return;
      }
    }

    // No hay perfil activo válido, seleccionar el primero
    const primerPerfil = usuario.perfiles[0];
    this.authStore.cambiarPerfilActivo(primerPerfil);
  }

  /**
   * Alterna la visibilidad del menú lateral en pantallas grandes
   * Deshabilita/habilita el split-pane para colapsar el menú
   */
  toggleSplitPane() {
    this.splitPaneDisabled = !this.splitPaneDisabled;
    
    // Si se colapsa el menú, cerrarlo
    if (this.splitPaneDisabled) {
      this.menuController.close('main-menu');
    }
  }

  /**
   * Navega a /gestion y, si hay un productor activo, también alterna el sub-menú.
   * En móvil (sin split-pane) cierra el menú tras navegar.
   */
  toggleGestion(): void {
    this.router.navigate(['/gestion']);
    if (this.productorActivoParaMenu) {
      this.gestionExpandido = !this.gestionExpandido;
    } else {
      this.menuController.close('main-menu');
    }
  }

  /** Indica si la ruta actual pertenece a la sección Gestión. */
  get esRutaGestion(): boolean {
    return this.router.url.startsWith('/gestion');
  }

  /** Actualiza si la ruta activa es pública (login, callback, etc.). */
  private actualizarEsRutaPublicaAuth(url?: string): void {
    const routerPath = normalizarRuta(url ?? this.router.url ?? '');
    const windowPath = typeof window !== 'undefined' ? normalizarRuta(window.location.pathname) : routerPath;
    this.esRutaPublicaAuth = esRutaPublicaAuth(routerPath) || esRutaPublicaAuth(windowPath);
  }

  /**
   * Habilita el menú solo cuando el shell autenticado debe mostrarse.
   * Al habilitar, se difiere un frame (rAF) para que Ionic reaplique el menú en el
   * split-pane tras salir de /login|/callback; un enable(true) síncrono puede quedar perdido.
   * No usar ApplicationRef.tick() aquí: anidar CD desde ngOnInit dispara NG0101.
   * Al ocultar, se deshabilita de inmediato (logout / ruta pública).
   */
  private sincronizarMenuSegunShell(): void {
    const show = this.mostrarShellAutenticado;
    if (!show) {
      void this.menuController.enable(false, 'main-menu');
      return;
    }

    // Tras re-login en la misma instancia SPA, no conservar el menú colapsado.
    this.splitPaneDisabled = false;
    requestAnimationFrame(() => {
      void this.menuController.enable(true, 'main-menu');
    });
  }

  /**
   * Rutas accesibles sin sesión (login, registro, documentos legales, etc.).
   * Compara Router y `location`: en carga directa el path del navegador ya es correcto
   * antes de que `router.url` refleje la misma ruta.
   */
  private estaEnRutaAccesoPublicoContraLocation(): boolean {
    const routerPath = normalizarRuta(this.router.url);
    const windowPath = typeof window !== 'undefined' ? normalizarRuta(window.location.pathname) : routerPath;
    return esRutaAccesoSinSesion(routerPath) || esRutaAccesoSinSesion(windowPath);
  }

  /**
   * Maneja el click del botón de menú:
   * - En pantallas pequeñas (< 992px): abre el menú overlay
   * - En pantallas grandes (≥ 992px): alterna entre expandir/colapsar el split-pane
   * Antes de actuar, re-habilita el menú si el shell está activo (recupera estado residual post-sesión).
   */
  async onMenuButtonClick() {
    if (this.mostrarShellAutenticado) {
      await this.menuController.enable(true, 'main-menu');
    }

    const isLargeScreen = window.innerWidth >= 992;

    if (isLargeScreen) {
      // En pantallas grandes, alternar el split-pane
      this.toggleSplitPane();
    } else {
      // En pantallas pequeñas, abrir el menú overlay
      await this.menuController.open('main-menu');
    }
  }
}
